import { useDispatch, useSelector } from "react-redux";

import { createNewLsUserSettings } from "core/local_storage_utils";
import { getCssValueWithUnit } from "core/str_utils";
import { getStorageUserSettings, userAdvActions } from "slices/user_adv";

/**
 * @callback useResizeSaveElemWidth
 * @param {number} width
 * @return {void}
 */

/**
 * Хук содержит логику для изменения ширины элемента.
 * Возвращаемый обработчик onMouseDown надо навесить на resizer
 * @param {object} props
 * @param {React.MutableRefObject.current} props.resizableElem - элемент
 * @param {number} props.minWidth - минимальная ширина элемента
 * @param {useResizeSaveElemWidth} [props.saveElemWidth] - функция сохранения ширины элемента
 */
export const useResize = ({ resizableElem, minWidth, saveElemWidth }) => {
	let originalWidth = 0;
	let originalMouseX = 0;

	const getElemWidth = () => parseFloat(getComputedStyle(resizableElem, null).getPropertyValue("width").replace("px", ""));

	const mouseMoveHandler = (e) => {
		if (!resizableElem) return;

		const width = originalWidth + (e.pageX - originalMouseX);
		if (width >= minWidth) {
			resizableElem.style.width = getCssValueWithUnit(width);
		}
	};

	const mouseUpHandler = () => {
		if (!resizableElem) return;

		resizableElem.style.transition = "";

		const width = getElemWidth();
		saveElemWidth?.(width);

		window.removeEventListener("mousemove", mouseMoveHandler);
		window.removeEventListener("mouseup", mouseUpHandler);
	};

	const mouseDownHandler = (e) => {
		if (!resizableElem) return;

		e.preventDefault();
		originalWidth = getElemWidth();
		originalMouseX = e.pageX;
		resizableElem.style.transition = "width 0ms";

		window.addEventListener("mousemove", mouseMoveHandler);
		window.addEventListener("mouseup", mouseUpHandler);
	};

	return { mouseDownHandler };
};

/**
 * Хук возвращает обработчики для ресайзера элемента
 * @param {Object} props
 * @param {HTMLElement} props.resizableElem - элемент
 * @param {boolean} [props.expanded] - состояние свернут/развернут элемента
 * @param {Function} [props.setExpanded] - сеттер состояния свернут/развернут элемента
 * @param {number} props.minWidth - минимальная ширина элемента
 * @param {number} [props.width] - ширина элемента
 * @param {Function} [props.setWidth] - сеттер ширины элемента
 * @param {string} [props.settingsPageName] - страница настроек в local storage
 * @param {string} [props.settingExpandedKey] - ключ в local storage для состояния свернут/развернут элемента
 * @param {string} [props.settingWidthKey] - ключ в local storage для ширины элемента
 * @returns
 */
export const useResizeHandlers = ({
	resizableElem,
	expanded,
	setExpanded,
	minWidth,
	width,
	setWidth,
	settingsPageName,
	settingExpandedKey,
	settingWidthKey,
}) => {
	const dispatch = useDispatch();
	const userSettings = useSelector(getStorageUserSettings);

	const toggleHandler = () => {
		if (!resizableElem) return;

		resizableElem.style.width = expanded ? getCssValueWithUnit(minWidth) : getCssValueWithUnit(width);

		const newUserSettings = createNewLsUserSettings({
			currentSettings: userSettings,
			pageName: settingsPageName,
			key: settingExpandedKey,
			value: !expanded,
		});

		dispatch(userAdvActions.setUserSettings(newUserSettings));
		setExpanded(!expanded);
	};

	const saveWidthHandler = (width) => {
		const expanded = width === minWidth ? false : true;

		setWidth(width);
		setExpanded(expanded);

		const newSettingsWithWidth = createNewLsUserSettings({
			currentSettings: userSettings,
			pageName: settingsPageName,
			key: settingWidthKey,
			value: width,
		});

		const newUserSettings = createNewLsUserSettings({
			currentSettings: newSettingsWithWidth,
			pageName: settingsPageName,
			key: settingExpandedKey,
			value: expanded,
		});

		dispatch(userAdvActions.setUserSettings(newUserSettings));
	};

	const initialWidthHandler = () => {
		return expanded ? getCssValueWithUnit(width) : getCssValueWithUnit(minWidth);
	};

	return { toggleHandler, saveWidthHandler, initialWidthHandler };
};
